import React from 'react';
import { FormFields, IForm, useForm } from '@hooks/useForm';

export interface IFilter<TFilter extends object> extends IForm<TFilter> {
    requestedFilter: TFilter
    updateFilter: (values: TFilter) => void
    resetFilter: VoidFunction
    handleFilterRequest: VoidFunction
}

export function useFilter<TFilter extends object>(fields: FormFields<TFilter>): IFilter<TFilter> {
    const form = useForm(fields);

    const [requestedFilter, setRequestedFilter] = React.useState<TFilter>(() => {
        const filter = {} as TFilter;
        form.forEachField((field, fieldKeyOf) => {
            filter[fieldKeyOf] = field.value;
        });

        return filter;
    });

    const updateRequestedFilter = React.useCallback(() => {
        setRequestedFilter(form.getInputValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateFilter = React.useCallback((values: TFilter) => {
        form.setValues(values);
        updateRequestedFilter();
    }, [form, updateRequestedFilter]);

    const resetFilter = React.useCallback(() => {
        form.reset();
        updateRequestedFilter();
    }, [form, updateRequestedFilter]);

    const handleFilterRequest = React.useCallback(() => {
        updateRequestedFilter();
    }, [updateRequestedFilter]);

    return React.useMemo(() => ({
        ...form,
        requestedFilter,
        resetFilter,
        updateFilter,
        handleFilterRequest
    }), [form, handleFilterRequest, resetFilter, requestedFilter, updateFilter]);
}